import React from 'react';
import { useParams } from 'react-router-dom';

import CardHeader from '../../../common/Labels/CardHeader';
import CardView from '../../../common/CardView/CardView';

import LogsPanel from '../../../LogsPanel/LogsPanel';
import TimeRangePicker from '../../../TimeRangePicker/TimeRangePicker';

import { usePreferredTimeRange } from '../../../contexts/TimeRangeContext/TimeRangeContext';

import InferenceLatencyChart from '../../../charts/prefabs/InferenceLatencyChart';
import InferenceRateChart from '../../../charts/prefabs/InferenceRateChart';
import ReplicaCountChart from '../../../charts/prefabs/ReplicaCountChart';
import RequestPerSecondChart from '../../../charts/prefabs/RequestPerSecondChart';
import OutputTokenChart from '../../../charts/prefabs/OutputTokenChart';
import SidekickReplicaCountChart from '../../../charts/prefabs/SidekickReplicaCountChart';
import TTFTChart from '../../../charts/prefabs/TTFTChart';

import useClientContext from '../../../contexts/ClientData/ClientData';
import usePipelineData from '../../PipelineDataContext';
import { usePipelineEdgeData } from '../../../VersionHistoryTimeline/PublishPipelineContext';

// Might require an update to the grid column styling to accommodate another row
export const PipelineMetricsTab = ({ externalUrl }) => {
  const routeParams = useParams();

  const { pipeline, deploymentPipelineVersions, isAnomalyDetectionEnabled } = usePipelineData();
  const { isEdgeEnabled } = useClientContext();
  const { isEdgeSelected } = usePipelineEdgeData();
  const {
    startTimestamp,
    endTimestamp,
    step,
    selectedTimeRange,
    onTimeRangeChange,
  } = usePreferredTimeRange()

  const labelFormatString = (endTimestamp - startTimestamp) > 24 * 60 * 60
    ? 'LLL d k:mm:ss'
    : 'k:mm:ss';

  const showCharts = !isEdgeEnabled || (!isEdgeSelected);

  // TODO - Source this from the latest version with deployments instead of just the latest version
  const backupDeploymentID = `${deploymentPipelineVersions?.deployment?.deploy_id}-${deploymentPipelineVersions?.deployment?.id}`;

  const urlParts = externalUrl?.length ? externalUrl.split('/') : [];
  const deploymentID = urlParts?.length ? urlParts[urlParts.length - 2] : backupDeploymentID;

  return (
    <>
      <CardView>
        <CardHeader className="mb-2">Viewing Pipeline Data for {routeParams.id}</CardHeader>
        <div className="flex flex-row">
          <TimeRangePicker selectedRange={selectedTimeRange} onChange={onTimeRangeChange} />
        </div>
      </CardView>
      {
        showCharts && (
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 mt-4 xl:grid-cols-2 grid-rows-chart-column xl:grid-rows-chart-grid">
            <RequestPerSecondChart
              chartID="requests-per-second"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <InferenceRateChart
              chartID="inference-rate"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <InferenceLatencyChart
              chartID="inference-latency"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <ReplicaCountChart
              chartID="replica-count"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <SidekickReplicaCountChart
              chartID="sidekick-replica-count"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <TTFTChart
              chartID="ttft-count"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
            <OutputTokenChart
              chartID="tps-count"
              pipelineID={pipeline?.pipelineID}
              deploymentID={deploymentID}
              pipelineVersion={routeParams?.version}
              labelFormatString={labelFormatString}
              startTimestamp={startTimestamp}
              endTimestamp={endTimestamp}
              step={step}
            />
          </div>
        )
      }
      <div
        className="grid grid-cols-1 grid-rows-single-card gap-x-6 gap-y-3 mt-8"
      >
        <LogsPanel pipelineID={routeParams.id} isAnomalyDetectionEnabled={isAnomalyDetectionEnabled} />
      </div>
    </>
  )
}

export default PipelineMetricsTab;
